/**
 * Headless helpers for the purchase ledger's pages (finance home, suppliers, open purchase orders, ledger rules):
 * formatting, and the choices a page offers. The views decide every business rule; nothing here re-derives one.
 */

import type {
  OpenOrderBucket,
  VatTreatment,
} from "../data/purchase-ledger-types";

export const OPEN_ORDER_BUCKETS: { bucket: OpenOrderBucket; label: string }[] =
  [
    { bucket: "received", label: "Received, no invoice" },
    { bucket: "part_received", label: "Part received" },
    { bucket: "not_received", label: "Ordered, not received" },
  ];

/** The ledger's VAT treatments, as a person picks one for a coding rule. */
export const VAT_TREATMENT_OPTIONS: { value: VatTreatment; label: string }[] = [
  { value: "standard", label: "Standard 20%" },
  { value: "reduced", label: "Reduced 5%" },
  { value: "zero_rated", label: "Zero-rated" },
  { value: "exempt", label: "Exempt" },
  { value: "no_vat", label: "No VAT (not registered)" },
  { value: "reverse_charge", label: "Reverse charge" },
];

/**
 * How long this has sat, as finance would say it: once nothing unconfirmed reaches Xero, age is an operational
 * number rather than a detail.
 */
export function ageLabel(receivedAt: string, now: Date): string {
  const hours = Math.max(
    0,
    Math.floor((now.getTime() - new Date(receivedAt).getTime()) / 3_600_000),
  );
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function formatMoney(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(amount);
}

/** "14 Aug 2026"; a missing date reads "—". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}
